import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  Key,
  Loader2,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { RoleFormPanel } from '../components/rbac/RoleFormPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const RBACPage = () => {
  const { t } = useTranslation('security');
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/roles`),
        axios.get(`${API_URL}/api/rbac/permissions`)
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('rbac.messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/rbac/roles`, data);
      toast.success(t('rbac.messages.created'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('rbac.messages.errorCreating'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/rbac/roles/${selectedRole.id}`, data);
      toast.success(t('rbac.messages.updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('rbac.messages.errorUpdating'));
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('rbac.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/rbac/roles/${id}`);
      toast.success(t('rbac.messages.deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('rbac.messages.errorDeleting'));
    }
  };

  return (
    <div className="space-y-6" data-testid="rbac-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Work_Sans',sans-serif]">
            {t('rbac.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('rbac.subtitle')}
          </p>
        </div>
        <Button 
          className="bg-[#E63946] hover:bg-[#C92A36]"
          onClick={() => { setSelectedRole(null); setIsFormOpen(true); }}
          data-testid="add-role-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('rbac.newRole')}
        </Button>
      </div>

      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles" data-testid="tab-roles">
            <Shield className="h-4 w-4 mr-2" />
            {t('rbac.tabs.roles')} ({roles.length})
          </TabsTrigger>
          <TabsTrigger value="permissions" data-testid="tab-permissions">
            <Key className="h-4 w-4 mr-2" />
            {t('rbac.tabs.permissions')} ({permissions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roles">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
              </div>
            ) : (
              roles.map((role) => (
                <Card key={role.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className={`h-5 w-5 ${role.is_system ? 'text-[#E63946]' : 'text-muted-foreground'}`} />
                        <CardTitle className="text-lg">{role.name}</CardTitle>
                      </div>
                      {role.is_system && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          {t('rbac.systemRole')}
                        </Badge>
                      )}
                    </div>
                    <CardDescription>{role.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        <Key className="h-4 w-4 inline mr-1" />
                        {role.permissions?.length || 0} {t('rbac.permissionCount')}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedRole(role); setIsFormOpen(true); }}
                          data-testid={`edit-role-${role.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {!role.is_system && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(role.id)}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="permissions">
          <Card>
            <CardHeader>
              <CardTitle>{t('rbac.permissions.title')}</CardTitle>
              <CardDescription>
                {t('rbac.permissions.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(
                  permissions.reduce((acc, perm) => {
                    if (!acc[perm.module]) acc[perm.module] = [];
                    acc[perm.module].push(perm);
                    return acc;
                  }, {})
                ).map(([module, perms]) => (
                  <div key={module}>
                    <h3 className="font-semibold text-foreground mb-3 capitalize flex items-center gap-2">
                      <Badge variant="outline">{module}</Badge>
                      <span className="text-sm text-muted-foreground">({perms.length})</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {perms.map((perm) => (
                        <div 
                          key={perm.id} 
                          className="p-3 bg-muted rounded-lg border border-border"
                        >
                          <div className="flex items-center gap-2">
                            <Key className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">{perm.name}</span>
                          </div>
                          <code className="text-xs text-muted-foreground mt-1 block">
                            {perm.code}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Form Panel */}
      <RoleFormPanel
        role={selectedRole}
        permissions={permissions}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={selectedRole ? handleUpdate : handleCreate}
      />
    </div>
  );
};

export default RBACPage;
